import * as React from "react";
import {
    Button,
    SpaceBetween,
    Header,
    Table,
    Box,
    Modal,
    FormField,
    Input,
    Select,
    Checkbox,
    Alert
} from "@amzn/awsui-components-react";
import { ConfigurableOption, AdditionalFieldConfig, PrimitiveDataFactory } from "@amzn/ask-legal-domain";
import { LegalContactConstants } from "../../utils/legal-contact.constant";

interface LegalContactField {
    id: string;
    name: string;
    type: "Default" | "System" | "Additional";
    info: string;
    isEditable: boolean;
    isDefault?: boolean;
    additionalFieldConfig?: AdditionalFieldConfig;
}

export interface LegalContactFieldsConfigPanelProps {
    scopeOptions: ConfigurableOption[];
    teamOptions: ConfigurableOption[];
    subTeamOptions: ConfigurableOption[];
    additionalFieldsConfig: AdditionalFieldConfig[];
    onScopeOptionsChange: (options: ConfigurableOption[]) => void;
    onTeamOptionsChange: (options: ConfigurableOption[]) => void;
    onSubTeamOptionsChange: (options: ConfigurableOption[]) => void;
    onAdditionalFieldsChange: (config: AdditionalFieldConfig[]) => void;
    loading?: boolean;
}

interface OptionRowProps {
    option: ConfigurableOption;
    index: number;
    loading: boolean;
    onUpdate: (index: number, value: string) => void;
    onToggleDeprecation: (index: number) => void;
}

// Reusable option row
const OptionRow: React.FC<OptionRowProps> = ({ option, index, loading, onUpdate, onToggleDeprecation }) => (
    <SpaceBetween direction="horizontal" size="xs">
        <Box color={option.deprecated ? "text-status-inactive" : undefined}>
            <Input
                value={option.displayValue}
                onChange={({ detail }) => onUpdate(index, detail.value)}
                placeholder="Enter option"
                disabled={loading || option.deprecated}
            />
        </Box>
        <Button
            variant="icon"
            iconName={option.deprecated ? "undo" : "close"}
            onClick={() => onToggleDeprecation(index)}
            disabled={loading}
            ariaLabel={option.deprecated ? "Click to undeprecate this option. It will become available for users to select again" : "Click to deprecate this option. It will be hidden from users but existing selections will be preserved"}
        />
    </SpaceBetween>
);

export const LegalContactFieldsConfigPanel: React.FC<LegalContactFieldsConfigPanelProps> = ({
    scopeOptions,
    teamOptions,
    subTeamOptions,
    additionalFieldsConfig,
    onScopeOptionsChange,
    onTeamOptionsChange,
    onSubTeamOptionsChange,
    onAdditionalFieldsChange,
    loading = false
}) => {
    const [showAddModal, setShowAddModal] = React.useState(false);
    const [showEditModal, setShowEditModal] = React.useState(false);
    const [showDefaultFieldModal, setShowDefaultFieldModal] = React.useState(false);
    const [editingField, setEditingField] = React.useState<LegalContactField | null>(null);
    const [editingDefaultField, setEditingDefaultField] = React.useState<LegalContactField | null>(null);
    const [newFieldName, setNewFieldName] = React.useState("");
    const [newFieldType, setNewFieldType] = React.useState<"choice" | "links">("choice");
    const [newFieldRequired, setNewFieldRequired] = React.useState(false);
    const [newFieldChoices, setNewFieldChoices] = React.useState<ConfigurableOption[]>([]);
    const [defaultFieldOptions, setDefaultFieldOptions] = React.useState<ConfigurableOption[]>([]);
    const [errors, setErrors] = React.useState<string[]>([]);

    // Fields list
    const legalContactFields: LegalContactField[] = React.useMemo(() => {
        const fields: LegalContactField[] = [
            {
                id: "legalContact",
                name: "Legal Contact",
                type: "Default",
                info: "The legal contact that supports the business",
                isEditable: false,
                isDefault: true
            },
            {
                id: "team",
                name: "Team",
                type: "Default",
                info: "Business team name",
                isEditable: true,
                isDefault: true
            },
            {
                id: "subTeam",
                name: "Sub-Team",
                type: "Default",
                info: "Business Sub Team Name",
                isEditable: true,
                isDefault: true
            },
            {
                id: "scope",
                name: "Scope",
                type: "Default",
                info: "List of Scope",
                isEditable: true,
                isDefault: true
            },
            {
                id: "manager",
                name: "Manager",
                type: "System",
                info: "Manager",
                isEditable: false,
                isDefault: true
            },
            {
                id: "officeLocation",
                name: "Office Location",
                type: "System",
                info: "Office Location",
                isEditable: false,
                isDefault: true
            },
            {
                id: "businessLeader",
                name: "Business Leader",
                type: "System",
                info: "Business Leader",
                isEditable: false,
                isDefault: true
            }
        ];

        // Additional fields
        additionalFieldsConfig.forEach((config, index) => {
            fields.push({
                id: `${LegalContactConstants.ADDITIONAL_FIELD_PREFIX}${config.fieldKey}`,
                name: config.name,
                type: "Additional",
                info: config.type === "choice" ? "Choice" : "Link",
                isEditable: true,
                isDefault: false,
                additionalFieldConfig: config
            });
        });

        return fields;
    }, [additionalFieldsConfig]);

    const generateFieldKey = (): string => {
        return PrimitiveDataFactory.id();
    };

    const validateField = (): boolean => {
        const newErrors: string[] = [];

        if (!newFieldName.trim()) {
            newErrors.push("Field name is required");
        }

        if (newFieldType === "choice") {
            const hasValidChoices = newFieldChoices.some(choice => choice.displayValue && choice.displayValue.trim());
            if (!hasValidChoices) {
                newErrors.push("Choice field must have at least one option");
            }
        }

        if (additionalFieldsConfig.length >= 10 && !editingField) {
            newErrors.push("Maximum 10 additional fields allowed");
        }

        setErrors(newErrors);
        return newErrors.length === 0;
    };

    const handleAddField = () => {
        if (!validateField()) return;

        const fieldKey = generateFieldKey();
        const newField: AdditionalFieldConfig = {
            name: newFieldName,
            type: newFieldType,
            required: newFieldRequired,
            fieldKey: fieldKey,
            choices: newFieldType === "choice" ? newFieldChoices : undefined
        };

        onAdditionalFieldsChange([...additionalFieldsConfig, newField]);
        handleCloseModal();
    };

    const handleEditField = (field: LegalContactField) => {
        if (field.additionalFieldConfig) {
            setEditingField(field);
            setNewFieldName(field.additionalFieldConfig.name);
            setNewFieldType(field.additionalFieldConfig.type);
            setNewFieldRequired(field.additionalFieldConfig.required);
            setNewFieldChoices(field.additionalFieldConfig.choices || []);
            setShowEditModal(true);
        }
    };

    const handleEditDefaultField = (field: LegalContactField) => {
        setEditingDefaultField(field);

        switch (field.id) {
            case "scope":
                setDefaultFieldOptions(scopeOptions);
                break;
            case "team":
                setDefaultFieldOptions(teamOptions);
                break;
            case "subTeam":
                setDefaultFieldOptions(subTeamOptions);
                break;
        }

        setShowDefaultFieldModal(true);
    };

    const handleUpdateDefaultField = () => {
        if (!editingDefaultField) return;

        switch (editingDefaultField.id) {
            case "scope":
                onScopeOptionsChange(defaultFieldOptions);
                break;
            case "team":
                onTeamOptionsChange(defaultFieldOptions);
                break;
            case "subTeam":
                onSubTeamOptionsChange(defaultFieldOptions);
                break;
        }

        handleCloseModal();
    };

    const addDefaultOption = () => {
        setDefaultFieldOptions([...defaultFieldOptions, createNewOption()]);
    };

    const updateDefaultOption = (index: number, displayValue: string) => {
        const updatedOptions = [...defaultFieldOptions];
        updatedOptions[index] = { ...updatedOptions[index], displayValue };
        setDefaultFieldOptions(updatedOptions);

        // Validate for duplicates and character limit
        validateOptions(updatedOptions);
    };

    const validateOptions = (options: ConfigurableOption[]) => {
        const newErrors: string[] = [];
        const seen = new Set<string>();

        options.forEach((option, index) => {
            // Check character limit
            if (option.displayValue && option.displayValue.length > 200) {
                newErrors.push(`Option ${index + 1}: Exceeded character limit of 200`);
            }

            // Check for duplicates
            if (option.displayValue && seen.has(option.displayValue.toLowerCase())) {
                newErrors.push(`Option "${option.displayValue}": Duplicate value detected`);
            } else if (option.displayValue) {
                seen.add(option.displayValue.toLowerCase());
            }
        });

        setErrors(newErrors);
    };

    const handleUpdateField = () => {
        if (!validateField() || !editingField?.additionalFieldConfig) return;

        const updatedField: AdditionalFieldConfig = {
            ...editingField.additionalFieldConfig,
            name: newFieldName,
            type: newFieldType,
            required: newFieldRequired,
            choices: newFieldType === "choice" ? newFieldChoices : undefined
        };

        const updatedConfig = additionalFieldsConfig.map(f =>
            f.fieldKey === editingField.additionalFieldConfig?.fieldKey ? updatedField : f
        );

        onAdditionalFieldsChange(updatedConfig);
        handleCloseModal();
    };

    const handleDeleteField = (field: LegalContactField) => {
        if (field.additionalFieldConfig) {
            const updatedConfig = additionalFieldsConfig.filter(f =>
                f.fieldKey !== field.additionalFieldConfig?.fieldKey
            );
            onAdditionalFieldsChange(updatedConfig);
        }
    };

    const createNewOption = (): ConfigurableOption => ({
        id: PrimitiveDataFactory.id(),
        displayValue: ""
    });

    const handleCloseModal = () => {
        setShowAddModal(false);
        setShowEditModal(false);
        setShowDefaultFieldModal(false);
        setEditingField(null);
        setEditingDefaultField(null);
        setNewFieldName("");
        setNewFieldType("choice");
        setNewFieldRequired(false);
        setNewFieldChoices([]);
        setDefaultFieldOptions([]);
        setErrors([]);
    };

    const addChoiceOption = () => {
        setNewFieldChoices([...newFieldChoices, createNewOption()]);
    };

    const updateChoiceOption = (index: number, displayValue: string) => {
        const updatedChoices = [...newFieldChoices];
        updatedChoices[index] = { ...updatedChoices[index], displayValue };
        setNewFieldChoices(updatedChoices);

        // Validate for duplicates and character limit
        validateOptions(updatedChoices);
    };

    const toggleOptionDeprecation = (index: number, isChoiceField: boolean) => {
        if (isChoiceField) {
            const updatedChoices = [...newFieldChoices];
            updatedChoices[index] = {
                ...updatedChoices[index],
                deprecated: !updatedChoices[index].deprecated
            };
            setNewFieldChoices(updatedChoices);
        } else {
            const updatedOptions = [...defaultFieldOptions];
            updatedOptions[index] = {
                ...updatedOptions[index],
                deprecated: !updatedOptions[index].deprecated
            };
            setDefaultFieldOptions(updatedOptions);
        }
    };

    return (
        <SpaceBetween size="l">
            <Header
                variant="h2"
                actions={
                    <Button
                        variant="primary"
                        onClick={() => setShowAddModal(true)}
                        disabled={loading || additionalFieldsConfig.length >= 10}
                    >
                        Add Field
                    </Button>
                }
            >
                Configure Container Fields
            </Header>

            <Table
                columnDefinitions={[
                    {
                        id: "name",
                        header: "Field name",
                        cell: (item: LegalContactField) => (
                            <Box color={item.type === "Default" ? "text-status-info" : undefined}>
                                {item.name}
                            </Box>
                        )
                    },
                    {
                        id: "type",
                        header: "Type",
                        cell: (item: LegalContactField) => item.type
                    },
                    {
                        id: "info",
                        header: "Info",
                        cell: (item: LegalContactField) => item.info
                    },
                    {
                        id: "required",
                        header: "Required",
                        cell: (item: LegalContactField) =>
                            item.additionalFieldConfig?.required ? "Yes" :
                            item.type === "Additional" ? "No" : ""
                    },
                    {
                        id: "actions",
                        header: "Actions",
                        cell: (item: LegalContactField) => (
                            <SpaceBetween direction="horizontal" size="xs">
                                {item.isEditable && (
                                    <Button
                                        variant="icon"
                                        iconName="edit"
                                        onClick={() => {
                                            if (item.type === "Additional") {
                                                handleEditField(item);
                                            } else if (item.type === "Default") {
                                                handleEditDefaultField(item);
                                            }
                                        }}
                                        disabled={loading}
                                        ariaLabel={`Edit ${item.name}`}
                                    />
                                )}
                                {item.type === "Additional" && (
                                    <Button
                                        variant="icon"
                                        iconName="remove"
                                        onClick={() => handleDeleteField(item)}
                                        disabled={loading}
                                        ariaLabel={`Delete ${item.name}`}
                                    />
                                )}
                            </SpaceBetween>
                        )
                    }
                ]}
                items={legalContactFields}
                loading={loading}
                stripedRows
            />

            {/* Add/Edit Field Modal */}
            <Modal
                visible={showAddModal || showEditModal}
                onDismiss={handleCloseModal}
                header={editingField ? "Edit Additional Field" : "Add Additional Field"}
                footer={
                    <Box float="right">
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button variant="link" onClick={handleCloseModal}>
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={editingField ? handleUpdateField : handleAddField}
                                disabled={loading}
                            >
                                {editingField ? "Update" : "Add"} Field
                            </Button>
                        </SpaceBetween>
                    </Box>
                }
            >
                <SpaceBetween size="l">
                    {errors.length > 0 && (
                        <Alert type="error" header="Validation Errors">
                            <ul>
                                {errors.map((error, index) => (
                                    <li key={index}>{error}</li>
                                ))}
                            </ul>
                        </Alert>
                    )}

                    <FormField label="Field Name" constraintText="Display name for the field">
                        <Input
                            value={newFieldName}
                            onChange={({ detail }) => setNewFieldName(detail.value)}
                            placeholder="Enter field name"
                            disabled={loading}
                        />
                    </FormField>

                    <FormField label="Field Type" constraintText="Type of data for this field">
                        <Select
                            selectedOption={{
                                label: newFieldType === "choice" ? "Choice" : "Link",
                                value: newFieldType
                            }}
                            onChange={({ detail }) => setNewFieldType(detail.selectedOption.value as "choice" | "links")}
                            options={[
                                { label: "Choice", value: "choice" },
                                { label: "Link", value: "links" }
                            ]}
                            disabled={loading || !!editingField}
                        />
                    </FormField>

                    <FormField label="Required Field">
                        <Checkbox
                            checked={newFieldRequired}
                            onChange={({ detail }) => setNewFieldRequired(detail.checked)}
                            disabled={loading}
                        >
                            This field is required
                        </Checkbox>
                    </FormField>

                    {newFieldType === "choice" && (
                        <FormField
                            label="Choice Options"
                            constraintText="Add choices for this field"
                        >
                            <SpaceBetween size="s">
                                {newFieldChoices.map((choice, index) => (
                                    <OptionRow
                                        key={index}
                                        option={choice}
                                        index={index}
                                        loading={loading}
                                        onUpdate={updateChoiceOption}
                                        onToggleDeprecation={(idx) => toggleOptionDeprecation(idx, true)}
                                    />
                                ))}
                                <Button
                                    variant="normal"
                                    iconName="add-plus"
                                    onClick={addChoiceOption}
                                    disabled={loading}
                                >
                                    Add Choice Option
                                </Button>
                            </SpaceBetween>
                        </FormField>
                    )}
                </SpaceBetween>
            </Modal>

            {/* Edit Default Field Modal */}
            <Modal
                visible={showDefaultFieldModal}
                onDismiss={handleCloseModal}
                header={`Edit ${editingDefaultField?.name} Options`}
                footer={
                    <Box float="right">
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button variant="link" onClick={handleCloseModal}>
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleUpdateDefaultField}
                                disabled={loading}
                            >
                                Update Options
                            </Button>
                        </SpaceBetween>
                    </Box>
                }
            >
                <SpaceBetween size="l">
                    <FormField
                        label={`${editingDefaultField?.name} Options`}
                        constraintText={`Add choices for ${editingDefaultField?.name}`}
                    >
                        <SpaceBetween size="s">
                            {defaultFieldOptions.map((option, index) => (
                                <OptionRow
                                    key={index}
                                    option={option}
                                    index={index}
                                    loading={loading}
                                    onUpdate={updateDefaultOption}
                                    onToggleDeprecation={(idx) => toggleOptionDeprecation(idx, false)}
                                />
                            ))}
                            <Button
                                variant="normal"
                                iconName="add-plus"
                                onClick={addDefaultOption}
                                disabled={loading}
                            >
                                Add Option
                            </Button>
                        </SpaceBetween>
                    </FormField>
                </SpaceBetween>
            </Modal>
        </SpaceBetween>
    );
};
